import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination, ChangeLog } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  ExternalAccount,
  ExternalAccountTopUpOption,
  LinkedClient,
  PspIntegration,
  PspPartner,
} from '../types';

import ROUTES from '../../../routes';
import { externalAccountsApi } from './services';

export interface ExternalAccounstListState {
  pagination?: Pagination;
  externalAccounts?: ExternalAccount[];
  externalAccount?: ExternalAccount;
  pspPartners?: PspPartner[];
  takeCount?: number;
  casp?: CASP[];
  pspIntegrations?: PspIntegration[];
  openPayNotificationVisible?: boolean;
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  linkedClients?: LinkedClient[];
  linkedClientsPagination?: Pagination;
  linkedClientsTakeCount?: number;
  linkedClientsErrorsList?: string;
  externalAccountsErrorsList?: string;
}

const initialState: ExternalAccounstListState = {};

export const externalAccountsListSlice = createSlice({
  name: 'externalAccountsList',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    setLinkedClientTakeCount: (state, action: PayloadAction<number>) => {
      state.linkedClientsTakeCount = action.payload;
    },
    clearExternalAccountState: (state) => {
      state.externalAccount = undefined;
      state.externalAccountTopUpOptions = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.externalAccountsErrorsList = undefined;
    },
    clearAccountsState: (state) => {
      state.externalAccounts = undefined;
      state.pagination = undefined;
    },
    clearLinkedClientsState: (state) => {
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
      state.linkedClientsErrorsList = undefined;
    },
    setOpenPayNotificationVisible: (state, action: PayloadAction<boolean>) => {
      state.openPayNotificationVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccountsList.matchPending,
      (state) => {
        state.externalAccountsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccountsList.matchRejected,
      (state, action: any) => {
        state.pagination = undefined;
        state.externalAccounts = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.externalAccountsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.externalAccountsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccountsList.matchFulfilled,
      (state, action: PayloadAction<Response<any>>) => {
        state.externalAccounts = action?.payload?.data?.edges?.map((item) => ({
          pspPartnerId: '-',
          currencyId: '-',
          paymentType: '-',
          accountId: '-',
          accountName: '-',
          accountType: '-',
          active: false,
          url: generatePath(ROUTES.EXTERNAL_ACCOUNT, {
            accountId: item.node.id,
          }),
          ...item.node,
        }));
        state.pagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccountChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getPspIntegrations.matchFulfilled,
      (state, action: PayloadAction<Response<PspIntegration>>) => {
        state.pspIntegrations = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getAccountsLinkedClients.matchPending,
      (state) => {
        state.linkedClientsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getAccountsLinkedClients.matchRejected,
      (state, action: any) => {
        state.linkedClients = undefined;
        state.linkedClientsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.linkedClientsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.linkedClientsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getAccountsLinkedClients.matchFulfilled,
      (state, action: PayloadAction<Response<LinkedClient>>) => {
        state.linkedClients = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
        state.linkedClientsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccountTopUpOptions
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExternalAccountTopUpOption>>) => {
        state.externalAccountTopUpOptions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.deletePaymentAccounts.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccountTopUpOption }>) => {
        state.externalAccountTopUpOptions =
          state?.externalAccountTopUpOptions?.filter(
            (item) => item.id !== action.payload?.data?.id,
          );
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.updateExternalAccountCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.currenciesIds = action.payload.data.currenciesIds;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getAccountsPspPartners.matchFulfilled,
      (state, action: PayloadAction<Response<PspPartner>>) => {
        state.pspPartners = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getExternalAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccount = action.payload.data;
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.getAccountsCASP.matchFulfilled,
      (state, action: PayloadAction<Response<CASP>>) => {
        state.casp = action?.payload?.data?.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      externalAccountsApi.endpoints.updateExternalAccountStatus.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                active: boolean;
                ids: string[];
              };
            };
          };
        },
      ) => {
        const nextExternalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action?.meta.arg?.originalArgs?.ids[0]) {
            result.active = action?.meta.arg?.originalArgs.active;
          }

          return result;
        });

        state.externalAccounts = nextExternalAccounts;
      },
    );
  },
});

export const {
  setTakeCount,
  clearExternalAccountState,
  setOpenPayNotificationVisible,
  setLinkedClientTakeCount,
  clearAccountsState,
  clearLinkedClientsState,
} = externalAccountsListSlice.actions;

export default externalAccountsListSlice.reducer;
